import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Send, Sparkles } from "lucide-react";

const STARTERS = [
  "Where are you right now?",
  "Best diner you've hit so far?",
  "What's the next drop?",
  "Tell me about Marfa at 1am.",
];

export default function ChatPage() {
  const [messages, setMessages] = useState([
    {
      id: "intro",
      role: "assistant",
      text: "Hey — BooBoo here. AI version, fully briefed. Ask me about the route, the gear, the weird stuff. What you wanna know?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [messages, sending]);

  const mkId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const send = async (text) => {
    if (!text.trim() || sending) return;
    setMessages((m) => [...m, { id: mkId(), role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/send", { message: text, session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { id: mkId(), role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { id: mkId(), role: "assistant", text: "Signal dropped. Try again in a sec." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 flex flex-col" data-testid="chat-page" style={{ minHeight: "calc(100vh - 64px)" }}>
      <div className="flex items-center gap-4 border-b border-white/10 pb-5">
        <div
          className="w-12 h-12 bg-cover bg-center border border-white/10"
          style={{
            backgroundImage:
              "url(https://customer-assets.emergentagent.com/job_journey-hub-283/artifacts/6ld7fx8l_file_00000000ae8871f5b0415f16ffc8ce51~2.png)",
          }}
        />
        <div>
          <div className="overline flex items-center gap-2">
            <Sparkles size={12} /> AI · GPT-5.2
          </div>
          <div className="font-display text-2xl uppercase">BooBoo · AI</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-6 space-y-3" data-testid="chat-thread">
        {messages.map((m, i) => (
          <div
            key={m.id}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`chat-msg-${m.role}-${i}`}
          >
            <div className={m.role === "user" ? "bubble-user" : "bubble-ai"}>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bubble-ai font-mono-tactical text-xs text-white/60">…transmitting</div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {STARTERS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="tac-card text-left text-sm p-3 hover:border-[#FF1493] transition"
              data-testid={`starter-${s.slice(0, 10)}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="flex gap-2 border-t border-white/10 pt-5"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask anything..."
          className="tac-input flex-1"
          data-testid="chat-input"
        />
        <button type="submit" disabled={sending} className="btn-blaze" data-testid="chat-send">
          <Send size={14} /> Send
        </button>
      </form>
    </div>
  );
}
